// Отгрузка по зеркалу документов 1С (ручка ядра /ut/*). Только правила показа и типы.
//
// ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ, ЕСЛИ ЕСТЬ lib/purchasing.ts.
// В purchasing лежит разбор СЫРЫХ документов 1С, приехавших через прокси: перевод статусов,
// разбор нулевого guid'а, склейка «ref → название». Это по-прежнему нужно — «Кабинет
// закупщика» читает 1С именно так, и справочники названий тоже приходят оттуда. Здесь же
// форма ответа ЯДРА, где всё уже разобрано, посчитано и отфильтровано в Postgres. Смешать
// их в одном файле значило бы завести два набора правил про одни и те же документы и
// однажды показать разные числа на соседних экранах.
//
// ЧЕГО ЗДЕСЬ НЕТ И НЕ БУДЕТ — НИ ОДНОГО СВОЕГО ПРАВИЛА ПРО ДАННЫЕ.
// Ни «ждёт отгрузки», ни «срок прошёл», ни итоги по выборке здесь не считаются. Всё это
// считает ядро (ut_api.py) ОДНИМ запросом на весь экран. Стоит завести здесь вторую такую
// формулу — и счётчик на кнопке вкладки разойдётся с числом строк под ней.

import { строкаЗапроса } from './chain'
import { orderStatusRu, shipmentStatusRu, shipWayRu } from './purchasing'

/** База ручек ядра через BFF. */
export const БАЗА = '/api/ut'

/** Вкладки раздела. Ключи — те же, что понимает ядро (ut_api.ВКЛАДКИ). */
export type Вкладка = 'shipped' | 'pending'

export interface ФильтрыОтгрузки {
  tab?: Вкладка
  /** Окно в днях. 0 — всё зеркало, и это дёшево: документов в нём около 2 500. */
  days?: number
  q?: string
  /** Ссылки контрагентов 1С. Экран находит их поиском по справочнику, ядро фильтрует по ним. */
  client?: string
  warehouse?: string
  unposted?: boolean
  start?: number
  limit?: number
}

export const ПУТИ = {
  справочники: () => '/refs',
  отгрузка: (ф: ФильтрыОтгрузки) => `/shipping${строкаЗапроса({ ...ф })}`,
}

/**
 * Окна, которые предлагает экран. 0 — «всё зеркало»; до переезда на ядро такого пункта не
 * было и быть не могло: 1С отдавала не больше 500 документов за запрос, и окно в год
 * молча обрезалось. Postgres отвечает по всей базе за те же доли секунды.
 */
export const ОКНА: Array<{ дней: number; ярлык: string }> = [
  { дней: 30, ярлык: 'последние 30 дней' },
  { дней: 90, ярлык: 'последние 90 дней' },
  { дней: 180, ярлык: 'последние 180 дней' },
  { дней: 0, ярлык: 'всё зеркало' },
]

export const РАЗМЕР_СТРАНИЦЫ = 50

/** Строка ответа ядра. Ссылки, а не имена: названий в зеркале нет (см. ut_api.py). */
export interface СтрокаОтгрузки {
  ref: string
  kind: 'sale' | 'order'
  number: string | null
  date: string | null
  status: string | null
  posted: boolean
  sum: number | null
  sum_settlement: number | null
  currency_ref: string | null
  warehouse_ref: string | null
  client_ref: string | null
  partner_ref: string | null
  manager_ref: string | null
  way: string | null
  address: string | null
  order_ref: string | null
  order_number: string | null
  order_date: string | null
  deal_ref: string | null
  /** Номер сделки Bitrix. null — в справочнике сделок 1С там не номер (см. deal_text). */
  deal_id: number | null
  /** Description сделки 1С, когда это не номер. Показываем как есть, а не выдаём за номер. */
  deal_text: string | null
  ship_plan_date: string | null
  overdue: boolean
  overdue_days: number | null
}

export interface ОтгрузкаОтвет {
  rows: СтрокаОтгрузки[]
  total: number
  next: number | null
  limit: number
  start: number
  tab: Вкладка
  /** Итоги по ВСЕЙ выборке, а не по странице. Ради них ручка и появилась. */
  totals: {
    count: number
    sum: number | null
    posted: number
    posted_sum: number | null
    first_doc: string | null
    last_doc: string | null
  }
  counts: {
    shipped: number
    pending: number
    /** Реализации без ссылки на заказ клиента: оформлены не «на основании». */
    orphan_sales: number
    late_orders: number
    /** Заказы, закрытые реализацией той же сделки, но не своей ссылкой. */
    closed_by_deal_only: number
  }
  warehouses: Array<{ ref: string; n: number }>
  window: { days: number | null; from: string | null; today: string }
  filters: {
    q: string | null
    deal_id: number | null
    warehouse: string | null
    clients: string[]
    unposted: boolean
  }
  mirror: {
    synced: Array<{
      entity: string; last_sync_at: string | null; last_count: number | null
      age_min: number | null
    }>
    age_min: number | null
    sales_since: string | null
    sales_last: string | null
    orders_since: string | null
    orders_last: string | null
  }
  data_notes: Record<string, string>
}

/**
 * Статус документа по-русски. Карты переводов берём из lib/purchasing — тех же самых,
 * которыми пользуется «Кабинет закупщика». Второй карты нет намеренно: разойдись они,
 * и один и тот же документ назывался бы на двух экранах по-разному.
 */
export const статусРу = (r: Pick<СтрокаОтгрузки, 'kind' | 'status'>): string =>
  (r.kind === 'sale' ? shipmentStatusRu(r.status) : orderStatusRu(r.status))

export const способРу = (v?: string | null): string => shipWayRu(v)

/**
 * Возраст зеркала словами. Это НЕ кеш на 300 секунд, как было у прокси, а прогон синка,
 * и путать их нельзя: «обновить» здесь ничего не ускорит — данные приедут со следующим
 * прогоном. Поэтому и текст другой.
 */
export function свежестьЗеркала(мин?: number | null): string {
  if (мин === null || мин === undefined) return 'синк зеркала 1С ещё ни разу не отмечался'
  if (мин < 1) return 'зеркало обновлено только что'
  if (мин < 60) return `зеркало обновлено ${Math.round(мин)} мин назад`
  const ч = Math.floor(мин / 60)
  return `зеркало обновлено ${ч} ч ${Math.round(мин - ч * 60)} мин назад`
}

/**
 * Оговорка о «ждёт отгрузки» — та, которую экран обязан повторить человеку.
 * Число берём из ответа ядра, а не считаем здесь: это факт о данных, а не оформление.
 */
export function оЖдущих(о?: ОтгрузкаОтвет): string {
  if (!о) return ''
  const n = о.counts.closed_by_deal_only
  const б = о.counts.orphan_sales
  return `«Ждёт отгрузки» — заказ клиента, по которому в 1С нет реализации ни своей ссылкой, `
    + `ни по общей сделке. Проверяются оба пути, потому что ${б} реализаций ссылки на заказ `
    + `не имеют вовсе (в 1С их оформили не «на основании»). Заказов, закрытых только вторым `
    + `путём, сейчас ${n} — по ссылке одной они выглядели бы неотгруженными.`
}
